import { ShipStats, ShipBuilderState } from "../../types/ship";
import { CalculationResult } from "../../types/calculations";
import { calculatePowerPlantStats } from "./powerCalculations";
import { calculateWeaponStats } from "./weaponCalculations";
import { calculateStateroomStats } from "./costCalculations";
import { calculateCrewRequirements } from "./crewCalculations";
import { calculateTotalCostFromDebugger } from "./costCalculations";
import { calculateTotalWeight } from "./weightCalculations";
import { calculatePowerRequired } from "./weightCalculations";
import { calculateFuelRequirements } from "./fuelCalculations";
import { calculateComputerStats } from "./computerCalculations";
import { DriveType } from "../../types/drive";

/**
 * Calculates comprehensive ship statistics
 * @param state - The ship builder state
 * @param optionalSystems - Optional systems data (from separate slice)
 * @returns Calculation result with ship stats or error
 */
export const calculateShipStats = (
  state: ShipBuilderState,
  optionalSystems?: any,
): CalculationResult<ShipStats> => {
  try {
    // Validate basic requirements
    if (!state.shipSize || state.shipSize <= 0) {
      return {
        success: false,
        error: {
          type: "INVALID_INPUT",
          message: "Ship size must be positive",
        },
      };
    }

    if (!state.hull) {
      return {
        success: false,
        error: {
          type: "INVALID_INPUT",
          message: "Hull must be selected",
        },
      };
    }

    // Calculate component stats
    const powerPlantStats = state.powerPlant
      ? calculatePowerPlantStats(
          state.powerPlant,
          state.shipSize,
          state.drives || [],
        )
      : {
          success: true,
          data: {
            cost: 0,
            tons: 0,
            power: 0,
            maintenanceCost: 0,
            powerRequired: 0,
          },
        };

    const weaponStats =
      state.weaponSelections && state.weaponSelections.length > 0
        ? calculateWeaponStats(state.weaponSelections as any[], {})
        : {
            success: true,
            data: { cost: 0, tons: 0, power: 0, hardpoints: 0, weaponCount: 0 },
            error: undefined,
          };

    const stateroomStats = state.stateroomConfig
      ? calculateStateroomStats(state.stateroomConfig)
      : {
          success: true,
          data: { cost: 0, tons: 0, totalRooms: 0 },
          error: undefined,
        };

    const crewRequirements = calculateCrewRequirements(state);

    // Calculate computer stats
    const computerStats = calculateComputerStats({ computer: state.computer });

    // Check for calculation errors
    if (!powerPlantStats.success)
      return { success: false, error: powerPlantStats.error };
    if (!weaponStats.success)
      return { success: false, error: weaponStats.error };
    if (!stateroomStats.success)
      return { success: false, error: stateroomStats.error };
    if (!crewRequirements.success)
      return { success: false, error: crewRequirements.error };
    if (!computerStats.success)
      return {
        success: false,
        error: {
          type: "INVALID_INPUT",
          message:
            computerStats.errors?.join(", ") || "Computer validation failed",
        },
      };

    // Calculate fuel requirements (default to 4 weeks if not specified)
    const fuelRequirements = calculateFuelRequirements(
      state.drives || [],
      state.powerPlant,
      state.shipSize,
      4, // Default to 1 month
    );

    // Calculate totals using the same method as CostDebugger (single source of truth)
    // Use the same hullAndArmorStats that CostDebugger uses (from Redux state)
    const totalCost = calculateTotalCostFromDebugger(
      state, 
      optionalSystems, 
      state.hullAndArmorStats, // Use the same data source as CostDebugger
      null // shipStats not needed for this calculation
    );
    const totalWeight = calculateTotalWeight(state, {}, optionalSystems);
    const powerRequired = calculatePowerRequired(state, {});

    if (!totalCost.success) return { success: false, error: totalCost.error };
    if (!totalWeight.success)
      return { success: false, error: totalWeight.error };
    if (!powerRequired.success)
      return { success: false, error: powerRequired.error };

    // Calculate cargo space (totalWeight now includes fuel tanks)
    const cargoSpace = state.shipSize - totalWeight.data!;

    return {
      success: true,
      data: {
        totalCost: totalCost.data!,
        totalWeight: totalWeight.data!,
        powerRequired: powerRequired.data!,
        cargoSpace: Math.max(0, cargoSpace),
        crewRequirements: Object.values(crewRequirements.data!).reduce(
          (sum, count) => sum + count,
          0,
        ),
        techLevel: state.techLevel || 8,
        jumpRange:
          state.drives?.find((d) => d.type === DriveType.JUMP)?.rating || 0,
        maneuverRating:
          state.drives?.find((d) => d.type === DriveType.MANEUVER)?.rating || 0,
        fuelRequired: fuelRequirements.total,
        powerRequirements: powerRequired.data!,
        totalCrew: Object.values(crewRequirements.data!).reduce(
          (sum, count) => sum + count,
          0,
        ),
        totalPassengers: 0, // TODO: Calculate from staterooms
        bridgeOfficers: 0, // TODO: Calculate if needed
        engineers: 0, // TODO: Calculate if needed
        gunners: 0, // TODO: Calculate if needed
        stewards: 0, // TODO: Calculate if needed
        totalPower: powerPlantStats.data!.power,
        size: state.shipSize,
        availableCargo: Math.max(0, cargoSpace),
        totalCargo: state.shipSize,
        usedCargo: totalWeight.data!,
        monthlyCost: 0, // TODO: Calculate monthly costs
        crewComposition: crewRequirements.data!,
        drives: state.drives || [],
        powerPlant: state.powerPlant!,
        bridge: state.bridge!,
        armor: state.armor!,
        sensors: state.sensors!,
        weapons: state.weaponSelections || [],
        weaponMounts: state.weaponMounts || [],
        stateroomConfig: state.stateroomConfig!,
        fuelTanks: state.fuelTanks || fuelRequirements.total,
        hull: state.hull,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate ship stats: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};
